import React, { useState } from "react";
import { m as motion } from "../../shared/components/Motion";
import "./qualification.css";
import QualificationSkeleton from "./QualificationSkeleton";
import { useQualificationData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditQualificationModal from "../../features/admin/modals/EditQualificationModal";
import {
  containerVariantsSlow as containerVariants,
  childVariantsSlow as childVariants,
  cld,
} from "../../shared/utils";

const COLLAPSED_ACHIEVEMENT_COUNT = 3;

const Qualification = () => {
  const [toggleState, setToggleState] = useState(1);
  const { data, loading } = useQualificationData();
  const { adminMode, isAdmin, qualificationDraft, saveQualificationDraft } =
    useAdmin();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState(new Set());

  // Use draft ONLY if admin is authenticated, otherwise use published data
  const displayData =
    isAdmin && qualificationDraft ? qualificationDraft : data || {};

  const toggleTab = (index) => {
    setToggleState(index);
  };

  const tabs = displayData?.tabs || [];

  const toggleExpand = (key) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleEditEntry = (tabIndex, entryIndex) => {
    setEditingTabIndex(tabIndex);
    setEditingEntryIndex(entryIndex);
    setEditingData(tabs[tabIndex]?.data[entryIndex] || null);
    setIsModalOpen(true);
  };

  const handleAddEntry = (tabIndex) => {
    setEditingTabIndex(tabIndex);
    setEditingEntryIndex(-1);
    setEditingData(null);
    setIsModalOpen(true);
  };

  const handleDeleteEntry = (tabIndex, entryIndex) => {
    if (window.confirm("Delete this entry?")) {
      const baseDraft = qualificationDraft || data || {};
      const tabsCopy = [...(baseDraft.tabs || [])];

      if (tabsCopy[tabIndex]) {
        const updatedData = tabsCopy[tabIndex].data.filter(
          (_, i) => i !== entryIndex,
        );
        tabsCopy[tabIndex] = {
          ...tabsCopy[tabIndex],
          data: updatedData,
        };

        saveQualificationDraft({ ...baseDraft, tabs: tabsCopy }).catch(
          (error) =>
            console.error("Error saving qualification draft:", error),
        );
      }
    }
  };

  const handleSaveEntry = () => {
    setIsModalOpen(false);
    setEditingTabIndex(null);
    setEditingEntryIndex(null);
    setEditingData(null);
  };

  const renderContent = (items, tabIndex) => {
    // Display newest first (as stored in DB)
    const displayItems = [...items];

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {displayItems.map((item, displayIndex) => {
          // Original index matches display index (no reversal)
          const originalIndex = displayIndex;
          const expandKey = `${tabIndex}-${displayIndex}`;
          const isExpanded = expandedEntries.has(expandKey);
          const hasAchievements = item.achievements?.length > 0;
          const hasTechStack = item.techStack?.length > 0;
          const isCurrent = item.calendar
            ?.toLowerCase()
            .includes("present");

          // Visible achievements based on expand state
          const visibleAchievements = isExpanded
            ? item.achievements
            : item.achievements?.slice(0, COLLAPSED_ACHIEVEMENT_COUNT);
          const remainingCount =
            (item.achievements?.length || 0) -
            COLLAPSED_ACHIEVEMENT_COUNT;

          // Backward-compatible company/location parsing
          const companyName =
            item.company || item.subtitle?.split(" - ")[0] || "";
          const locationStr =
            item.location ||
            (item.subtitle?.includes(" - ")
              ? item.subtitle.split(" - ").slice(1).join(" - ")
              : "");

          const entryContent = (
            <motion.div
              variants={childVariants}
              className="qualification__entry"
            >
              {/* Card */}
              <div className="qualification__card glass__card">
                <div className="qualification__card-header">
                  {item.logo ? (
                    <img
                      className="qualification__logo"
                      src={cld(item.logo, 80)}
                      alt={companyName}
                      loading="lazy"
                    />
                  ) : (
                    <div className="qualification__logo-fallback">
                      <i className="bx bx-buildings"></i>
                    </div>
                  )}
                  <div className="qualification__card-info">
                    <h3 className="qualification__title">{item.title}</h3>
                    {companyName && (
                      <span className="qualification__company">
                        {companyName}
                        {item.employmentType && (
                          <span className="qualification__employment-type">
                            {" "}
                            · {item.employmentType}
                          </span>
                        )}
                      </span>
                    )}
                    {locationStr && (
                      <span className="qualification__location">
                        <i className="uil uil-location-point"></i>
                        {locationStr}
                      </span>
                    )}
                    <div className="qualification__calendar">
                      <i className="uil uil-calendar-alt"></i>
                      {item.calendar}
                      {isCurrent && (
                        <span className="qualification__badge">
                          Current
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {adminMode && (
                  <div className="qualification__card-actions">
                    <motion.button
                      onClick={() =>
                        handleEditEntry(tabIndex, originalIndex)
                      }
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      className="qualification__action-btn"
                      title="Edit entry"
                    >
                      <i className="bx bx-pencil"></i>
                    </motion.button>
                    <motion.button
                      onClick={() =>
                        handleDeleteEntry(tabIndex, originalIndex)
                      }
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      className="qualification__action-btn"
                      title="Delete entry"
                    >
                      <i className="bx bx-trash"></i>
                    </motion.button>
                  </div>
                )}

                {/* Key Achievements (Inside Card) */}
                {hasAchievements && (
                  <div className="qualification__card-section">
                    <h4 className="qualification__section-heading">
                      <i className="bx bx-star"></i> Key Achievements
                    </h4>
                    <ul className="qualification__achievement-list">
                      {visibleAchievements.map((ach, i) => (
                        <li
                          key={i}
                          className="qualification__achievement-item"
                        >
                          {ach}
                        </li>
                      ))}
                    </ul>
                    {remainingCount > 0 && (
                      <button
                        className="qualification__show-more"
                        onClick={() => toggleExpand(expandKey)}
                      >
                        {isExpanded
                          ? "Show less"
                          : `+${remainingCount} more`}
                      </button>
                    )}
                  </div>
                )}

                {/* Tech Stack (Inside Card) */}
                {hasTechStack && (
                  <div className="qualification__card-section">
                    <h4 className="qualification__section-heading">
                      <i className="bx bx-code-alt"></i> Tech Stack
                    </h4>
                    <div className="qualification__techstack-chips">
                      {item.techStack.map((tech, i) => (
                        <span
                          key={i}
                          className="qualification__techstack-chip"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          );

          const timelineElements = (
            <motion.div variants={childVariants}>
              <span className="qualification__rounder"></span>
              <span className="qualification__line"></span>
            </motion.div>
          );

          return (
            <motion.div
              className={`qualification__data ${
                displayIndex % 2 === 0
                  ? "qualification__data--even"
                  : "qualification__data--odd"
              }`}
              key={displayIndex}
              variants={childVariants}
            >
              {displayIndex % 2 === 0 ? (
                <>
                  {entryContent}
                  {timelineElements}
                  <div className="qualification__empty-col"></div>
                </>
              ) : (
                <>
                  <div className="qualification__empty-col"></div>
                  {timelineElements}
                  {entryContent}
                </>
              )}
            </motion.div>
          );
        })}

        {/* Plus Button at the End - As a node on the chain */}
        {adminMode && (
          <motion.div
            className={`qualification__data qualification__data--add ${
              items.length === 0 ? "qualification__data--add--empty" : ""
            }`}
            variants={childVariants}
          >
            <div className="qualification__empty-col"></div>
            <motion.div
              className="qualification__timeline-add"
              variants={childVariants}
            >
              <motion.button
                className="admin__addNew-btn qualification__add-btn glass__button"
                onClick={() => handleAddEntry(tabIndex)}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                title="Add new entry"
              >
                <i className="bx bx-plus"></i>
              </motion.button>
            </motion.div>
            <div className="qualification__empty-col"></div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      {loading || !displayData.tabs ? (
        <QualificationSkeleton data={displayData} />
      ) : (
        <motion.section
          className="qualification section"
          id="qualification"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.h2 className="section__title" variants={childVariants}>
            Qualification
          </motion.h2>
          <motion.span className="section__subtitle" variants={childVariants}>
            My personal journey
          </motion.span>
          <motion.div
            className="qualification__container container"
            variants={containerVariants}
          >
            <motion.div
              className="qualification__tabs"
              variants={containerVariants}
            >
              {tabs.map((tab, idx) => (
                <motion.div
                  key={idx}
                  className={
                    toggleState === idx + 1
                      ? "qualification__button qualification__active button--flex"
                      : "qualification__button button--flex"
                  }
                  onClick={() => toggleTab(idx + 1)}
                  variants={childVariants}
                >
                  <i className={tab.icon}></i>
                  {tab.title}
                </motion.div>
              ))}
            </motion.div>
            <motion.div
              className="qualification__sections"
              variants={containerVariants}
            >
              {tabs.map((tab, idx) => (
                <motion.div
                  key={idx}
                  className={
                    toggleState === idx + 1
                      ? "qualification__content qualification__content-active"
                      : "qualification__content"
                  }
                  variants={childVariants}
                >
                  {toggleState === idx + 1
                    ? renderContent(tab.data || [], idx)
                    : null}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.section>
      )}

      <EditQualificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entryData={editingData}
        onSave={handleSaveEntry}
        tabIndex={editingTabIndex}
        entryIndex={editingEntryIndex}
        publishedData={data}
      />
    </>
  );
};

export default Qualification;
